import React from 'react';
// 색 상수는 `@ui/overlay/overlayConstants` 한 곳에서 온다.
// 종전엔 파일마다 같은 값을 손으로 들고 있어 `GOLD` 가 7갈래로 갈렸고, 「금색 통일」
// 리팩터가 세 파일을 놓친 것을 **아무도 몰랐다** — 색은 틀려도 에러를 안 낸다.
import { GOLD, ACCENT } from '@ui/overlay/overlayConstants';
import { reportEventNow } from '@engine/clientLogReporter';


// 루트 안전망 — 앱 어디서든(로그인·게임·기타 화면) 안 잡힌 렌더 에러가 터지면 React 18은
// 트리 전체를 언마운트해 화이트스크린이 된다(2026-07-18: /login 에서 크롬 자동완성/React DOM
// 충돌로 반복 확인, back/logs/client_debug — "NotFoundError: insertBefore" 2일간 다발).
// 이 바운더리가 그 크래시를 잡아 (1) 기존 client_debug 파이프라인으로 서버에 자동 기록하고
// (2) 백지 대신 원인이 보이는 복구 화면을 보여준다. GameCanvas 안쪽 렌더 실패(지형 등)는
// renderDiagnosticsBus/RenderStatusBanner 가 이미 담당 — 이 바운더리는 그 바깥, 앱 전체용.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const stack = (info?.componentStack || '').trim().split('\n').slice(0, 6).join(' › ');
    // level='react-crash' — 기존 render-diagnostic/onerror 와 구분해서 나중에 grep 하기 쉽게.
    const scope = this.props.scope ? `[${this.props.scope}] ` : '';
    reportEventNow('react-crash', `${scope}${error?.name || 'Error'}: ${error?.message || String(error)} | at: ${stack}`);
  }

  handleReload = () => window.location.reload();

  render() {
    if (!this.state.error) return this.props.children;
    const { error } = this.state;

    // ── 국소 바운더리 ────────────────────────────────────────────
    // `scope` 가 있으면 **그 서브트리만** 접고 나머지는 살려 둔다.
    //
    // [왜] 이 파일은 원래 루트 하나뿐이었고(main.jsx), 그래서 HUD 컴포넌트의 오타
    //   하나가 트리 전체를 재생성해 **GameCanvas 를 언마운트**시켰다. 언마운트는 곧
    //   `webglcontextlost` 이고, 그때 잃는 건 화면이 아니라 **월드를 다시 굽는 값**이다
    //   (실측: 블록지형 굽기 누적 141초 · LOD 80초). 2026-08-28 실사고에서
    //   `ReferenceError: Shirt is not defined`(HMR 데싱크) 하나가 정확히 그 경로를 탔다 —
    //   진단문의 「화면나감(언마운트)」이 그 서명이다(`GameCanvas.jsx` unmountingRef).
    //
    // ⚠ 루트 바운더리는 `scope` 없이 그대로 둔다 — 앱 전체가 못 서는 실패는
    //   숨기면 안 된다. 국소화는 **3D 를 지키기 위한 것**이지 오류를 덮는 게 아니라서,
    //   서버 기록(`componentDidCatch`)은 양쪽 다 똑같이 남는다.
    if (this.props.scope) {
      return (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 'max(12px, env(safe-area-inset-bottom))',
            zIndex: 999999,
            maxWidth: 'min(92vw, 460px)',
            padding: '10px 14px',
            borderRadius: 6,
            background: 'rgba(10,14,20,0.94)',
            border: '1px solid #ff6b6b',
            color: '#e8ecf0',
            fontFamily: "'Pretendard', system-ui, -apple-system, sans-serif",
            fontSize: 12,
            lineHeight: 1.55,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: GOLD, marginBottom: 2 }}>
              {this.props.scope} 가 멈췄습니다 — 3D 화면은 살아 있습니다
            </div>
            <div
              style={{
                color: 'rgba(232,236,240,0.5)',
                fontSize: 'var(--ak-fs-xs, 10px)',
                wordBreak: 'break-word',
                fontFamily: 'ui-monospace, "SF Mono", Consolas, monospace',
              }}
            >
              {error?.name || 'Error'}: {error?.message || String(error)}
            </div>
          </div>
          <button
            onClick={this.handleReload}
            style={{
              flexShrink: 0,
              padding: '6px 12px',
              borderRadius: 4,
              cursor: 'pointer',
              background: `linear-gradient(135deg, ${ACCENT}33, ${ACCENT}14)`,
              border: `1px solid ${ACCENT}`,
              color: ACCENT,
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "'Pretendard', system-ui, sans-serif",
            }}
          >
            새로고침
          </button>
        </div>
      );
    }

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 999999,
          background: 'radial-gradient(ellipse at 50% 30%, rgba(20,28,40,1), rgba(6,9,14,1))',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
          // 'Noto Sans KR' 은 이제 아무도 로드하지 않는다(Google @import 제거) —
          // 이름만 남아 조용히 폴백하던 자리다. 여기서 @ui/fonts 를 import 하지 않는 이유는
          // **에러 경계**라서다: 이 컴포넌트가 살아 있어야 흰 화면을 막는데, import 하나를
          // 늘리면 그게 깨졌을 때 마지막 그물까지 같이 죽는다. 그래서 값만 직접 적는다.
          fontFamily: "'Pretendard', system-ui, -apple-system, sans-serif",
          color: '#e8ecf0',
        }}
      >
        <div style={{ fontSize: 34 /* size-ok */, marginBottom: 14, color: '#ff6b6b' }}>■</div>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: GOLD }}>
          문제가 발생했습니다
        </div>
        <div style={{ fontSize: 13, color: 'rgba(232,236,240,0.65)', maxWidth: 420, lineHeight: 1.6, marginBottom: 6 }}>
          예상치 못한 오류로 화면이 멈췄습니다. 자동으로 서버에 기록됐습니다.
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'rgba(232,236,240,0.45)',
            maxWidth: 420,
            marginBottom: 22,
            wordBreak: 'break-word',
            fontFamily: 'ui-monospace, "SF Mono", Consolas, monospace',
          }}
        >
          {error?.name || 'Error'}: {error?.message || String(error)}
        </div>
        <button
          onClick={this.handleReload}
          style={{
            padding: '10px 26px',
            borderRadius: 4,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${ACCENT}33, ${ACCENT}14)`,
            border: `1px solid ${ACCENT}`,
            color: ACCENT,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: "'Pretendard', system-ui, sans-serif",
          }}
        >
          새로고침
        </button>
      </div>
    );
  }
}
